import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import { RowIcon } from '../../components/BopRowActions';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { usePermesso } from '../../context/PermessiContext';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import { formatCurrency } from '../../utils/formatters';
import { API_DOCS } from './BudgetContabAnaliticaPage.api';
import './BudgetContabAnaliticaPage.css';

const MODULO = 'controllo_gestione.budget_analitico';

type Natura = 'COSTO' | 'RICAVO';

interface ContoNode {
  id: number;
  codice: string;
  descrizione: string;
  natura: Natura;
  attivo: boolean;
  livello: number;
  divisioneId: number | null;
  figli: ContoNode[];
}

interface BudgetRow { contoAnaliticoId: number; importo: number; }
interface Divisione { id: number; codice: string; descrizione: string; attivo: boolean; }

const LIVELLO_LABEL: Record<number, string> = { 1: 'Centro', 2: 'Conto', 3: 'Sottoconto' };

const parseImporto = (s: string | undefined): number | null => {
  if (s == null) return null;
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t.replace(',', '.'));
  if (!Number.isFinite(n) || n === 0) return null;
  return n;
};

const annoCorrente = new Date().getFullYear();
const ANNI: number[] = [];
for (let a = annoCorrente + 1; a >= annoCorrente - 4; a--) ANNI.push(a);

export default function BudgetContabAnaliticaPage() {
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');

  const { societaId, setSocietaId, listaSocieta, isUnica } = useFiltroSocieta();

  const [anno, setAnno] = useState<number>(annoCorrente);
  const [tree, setTree] = useState<ContoNode[]>([]);
  const [divisioni, setDivisioni] = useState<Divisione[]>([]);
  const [filtroDivisioneId, setFiltroDivisioneId] = useState('');
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [initialSnapshot, setInitialSnapshot] = useState<string>('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const isDirty = initialSnapshot !== '' && JSON.stringify(edits) !== initialSnapshot;
  useDirtyForm(isDirty, `Budget contab. analitica ${anno}`);

  const collectExpandable = (nodes: ContoNode[], acc: Set<number>) => {
    for (const n of nodes) {
      if (n.figli.length) { acc.add(n.id); collectExpandable(n.figli, acc); }
    }
  };

  const load = async () => {
    if (!societaId) { setTree([]); setEdits({}); setInitialSnapshot(''); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      // @api GET /api/conti-analitici?societaId= -> { success: true, data: [{ id, codice, descrizione, natura, attivo, livello, divisioneId, figli }] }
      const contiRes = await apiFetch<{ success: boolean; data: ContoNode[] }>(
        `/api/conti-analitici?societaId=${societaId}`,
      );
      // @api GET /api/budget-conto-analitico?societaId=&anno= -> { success: true, data: [{ contoAnaliticoId, importo }] }
      const budgetRes = await apiFetch<{ success: boolean; data: BudgetRow[] }>(
        `/api/budget-conto-analitico?societaId=${societaId}&anno=${anno}`,
      );
      // @api GET /api/divisioni-analitiche?societaId= -> { success: true, data: [{ id, codice, descrizione, attivo }] }
      const divRes = await apiFetch<{ success: boolean; data: Divisione[] }>(
        `/api/divisioni-analitiche?societaId=${societaId}`,
      );

      const data = contiRes.data ?? [];
      setTree(data);
      setDivisioni(divRes.data ?? []);

      const budgetByConto = new Map<number, number>();
      for (const r of budgetRes.data ?? []) budgetByConto.set(r.contoAnaliticoId, r.importo);

      const nuovoEdits: Record<number, string> = {};
      const walkLeaves = (nodes: ContoNode[]) => {
        for (const n of nodes) {
          if (n.figli.length === 0) {
            if (n.attivo) {
              const v = budgetByConto.get(n.id);
              nuovoEdits[n.id] = v != null ? String(v) : '';
            }
          } else {
            walkLeaves(n.figli);
          }
        }
      };
      walkLeaves(data);
      setEdits(nuovoEdits);
      setInitialSnapshot(JSON.stringify(nuovoEdits));

      const exp = new Set<number>();
      collectExpandable(data, exp);
      setExpanded(exp);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [societaId, anno]);

  // ── Guardia modifiche non salvate sul cambio anno/società di pagina ───────
  const confermaScarto = () => !isDirty || window.confirm('Ci sono modifiche al budget non salvate. Continuare e perderle?');

  const cambiaAnno = (nuovo: number) => { if (confermaScarto()) setAnno(nuovo); };
  const cambiaSocieta = (id: string) => { if (confermaScarto()) setSocietaId(id); };

  // ── Toggle espansione ─────────────────────────────────────────────────────
  const toggle = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ── Aggregazione: totale budget di un nodo (somma foglie attive) ──────────
  const aggregato = (node: ContoNode): number => {
    if (node.figli.length === 0) return node.attivo ? (parseImporto(edits[node.id]) ?? 0) : 0;
    return node.figli.reduce((sum, f) => sum + aggregato(f), 0);
  };

  // ── Totali per natura ──────────────────────────────────────────────────────
  const totali = useMemo(() => {
    let costi = 0;
    let ricavi = 0;
    const walk = (nodes: ContoNode[]) => {
      for (const n of nodes) {
        if (n.figli.length === 0) {
          if (n.attivo) {
            const v = parseImporto(edits[n.id]) ?? 0;
            if (n.natura === 'COSTO') costi += v; else ricavi += v;
          }
        } else {
          walk(n.figli);
        }
      }
    };
    walk(tree);
    return { costi, ricavi, saldo: ricavi - costi };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, edits]);

  const setImporto = (contoId: number, value: string) => {
    setEdits(prev => ({ ...prev, [contoId]: value }));
  };

  const salva = async () => {
    if (!societaId) return;
    setSaving(true);
    setError('');
    try {
      const voci = Object.keys(edits).map(k => {
        const id = Number(k);
        return { contoAnaliticoId: id, importo: parseImporto(edits[id]) };
      });
      // @api PUT /api/budget-conto-analitico { societaId, anno, voci: [{ contoAnaliticoId, importo }] } -> { success: true, data: [{ contoAnaliticoId, importo }] }
      await apiFetch('/api/budget-conto-analitico', {
        method: 'PUT',
        body: JSON.stringify({ societaId: Number(societaId), anno, voci }),
      });
      // Ricarica lo stato canonico e azzera il dirty
      await load();
    } catch (e: any) {
      setError(e.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ricorsivo righe ─────────────────────────────────────────────────
  const renderRows = (nodes: ContoNode[]): React.ReactNode[] => {
    const out: React.ReactNode[] = [];
    for (const n of nodes) {
      if (!n.attivo) continue;
      const isLeaf = n.figli.length === 0;
      const isOpen = expanded.has(n.id);
      out.push(
        <tr key={n.id} className={isLeaf ? '' : 'bca-row--parent'}>
          <td>
            <div className={`bca-codice-cell bca-codice-cell--liv${n.livello}`}>
              {!isLeaf ? (
                <button
                  type="button"
                  className={`bca-toggle ${isOpen ? 'bca-toggle--open' : ''}`}
                  onClick={() => toggle(n.id)}
                  aria-label={isOpen ? 'Comprimi' : 'Espandi'}
                >
                  <RowIcon.Chevron />
                </button>
              ) : (
                <span className="bca-toggle-spacer" />
              )}
              <span className="bca-codice">{n.codice}</span>
            </div>
          </td>
          <td>{n.descrizione} <span className="bca-livello-tag">{LIVELLO_LABEL[n.livello]}</span></td>
          <td>
            <span className={`bop-badge ${n.natura === 'RICAVO' ? 'bop-badge--success' : 'bop-badge--warning'}`}>
              {n.natura === 'RICAVO' ? 'Ricavo' : 'Costo'}
            </span>
          </td>
          <td className="bop-text-right">
            {isLeaf ? (
              <input
                className="bop-input bca-importo-input"
                type="number"
                step="0.01"
                placeholder="0,00"
                value={edits[n.id] ?? ''}
                onChange={e => setImporto(n.id, e.target.value)}
                disabled={!canEdit}
              />
            ) : (
              <span className="bca-aggregato">{formatCurrency(aggregato(n))}</span>
            )}
          </td>
        </tr>,
      );
      if (!isLeaf && isOpen) out.push(...renderRows(n.figli));
    }
    return out;
  };

  const noSocieta = !societaId;
  const treeFiltrato = useMemo(() => {
    if (!filtroDivisioneId) return tree;
    const did = Number(filtroDivisioneId);
    return tree.filter(c => c.divisioneId === did);
  }, [tree, filtroDivisioneId]);
  const rows = renderRows(treeFiltrato);

  return (
    <Layout>
      <div className="budget-contab-analitica">
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2>Budget contabilità analitica</h2>
            <p>Valorizza le voci del piano dei conti per esercizio (salvataggio anche parziale)</p>
          </div>
          {canEdit && (
            <button className="bop-btn bop-btn-primary" onClick={salva} disabled={noSocieta || saving || !isDirty}>
              {saving ? 'Salvo…' : 'Salva budget'}
            </button>
          )}
        </div>

        <div className="bop-filter-bar">
          {!isUnica && (
            <select className="bop-select" value={societaId} onChange={e => cambiaSocieta(e.target.value)}>
              <option value="">Seleziona società…</option>
              {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          )}
          <label className="bca-filter-field">
            <span className="bca-filter-label">Esercizio</span>
            <select className="bop-select" value={anno} onChange={e => cambiaAnno(Number(e.target.value))}>
              {ANNI.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
          {divisioni.length > 0 && (
            <label className="bca-filter-field">
              <span className="bca-filter-label">Divisione</span>
              <select className="bop-select" value={filtroDivisioneId} onChange={e => setFiltroDivisioneId(e.target.value)}>
                <option value="">Tutte</option>
                {divisioni.map(d => <option key={d.id} value={d.id}>{d.codice} — {d.descrizione}</option>)}
              </select>
            </label>
          )}
          {isDirty && <span className="bca-dirty-hint">Modifiche non salvate</span>}
        </div>

        {error && <div className="bop-inline-error">{error}</div>}

        <div className="bop-card">
          <div className="bop-table-wrapper">
            {noSocieta ? (
              <div className="bop-empty-state">Seleziona una società per impostare il budget.</div>
            ) : loading ? (
              <div className="bop-empty-state">Caricamento…</div>
            ) : rows.length === 0 ? (
              <div className="bop-empty-state">Nessun conto attivo. Crea prima il piano dei conti analitico.</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Codice</th>
                    <th>Descrizione</th>
                    <th>Natura</th>
                    <th className="bop-text-right">Budget {anno} (€)</th>
                  </tr>
                </thead>
                <tbody>{rows}</tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} className="bop-text-right"><strong>Totale costi a budget</strong></td>
                    <td className="bop-text-right"><strong>{formatCurrency(totali.costi)}</strong></td>
                  </tr>
                  <tr>
                    <td colSpan={3} className="bop-text-right"><strong>Totale ricavi a budget</strong></td>
                    <td className="bop-text-right"><strong>{formatCurrency(totali.ricavi)}</strong></td>
                  </tr>
                  <tr>
                    <td colSpan={3} className="bop-text-right"><strong>Saldo (ricavi − costi)</strong></td>
                    <td className={`bop-text-right ${totali.saldo < 0 ? 'bop-text-danger' : ''}`}><strong>{formatCurrency(totali.saldo)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
